import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { Observable, catchError, map, tap, throwError } from 'rxjs'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { LanguageService } from '@app/core/service/language.service'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { environment } from '@envs/environment'
import { untilDestroyed } from '@app/shared/utils/utils'

@Injectable({ providedIn: 'root' })
export class FolderExportDocumentService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl
  private readonly destroy$
  private warningDialogRef: MatDialogRef<any, any>

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private lang: LanguageService
  ) {
    this.destroy$ = untilDestroyed()
  }

  downloadFile(url: string, scopeName: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        const a = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)

        a.href = objectUrl
        a.download = this.getFilenameFromUrl(url, scopeName) // Extract filename based on URL
        document.body.appendChild(a)
        a.click()

        URL.revokeObjectURL(objectUrl)
        document.body.removeChild(a)

        return blob
      }),
      catchError((error: any) => {
        console.error('Download error:', error)
        this.snackbarService.showSnackbar('Download error', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR)
        return throwError(() => new Error('Failed to download file'))
      })
    )
  }

  showWarningModal() {
    this.warningDialogRef = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Export ${this.lang.get('sow')}`,
        body: 'Your document is exporting, please DO NOT CLICK ‘download’ again. Check your Notifications tab in a few minutes for the download',
        confirmText: 'OK',
        confirmCallback: () => this.warningDialogRef.close(),
        limitBodyWidth: true,
      },
    })
  }

  closeWarningModal() {
    this.warningDialogRef?.close()
  }

  exportToDoc(id: number, name: string) {
    this.showWarningModal()
    this.downloadFile(`${this.BASE_API_URL}sow/${id}/export/docx`, name)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportToExcel(id: number, name: string) {
    this.showWarningModal()
    this.downloadFile(`${this.BASE_API_URL}sow/${id}/export/xlsx`, name)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportDepartmentsHoursXlsxTemplate(id: number, _name: string) {
    this.showWarningModal()
    this.http
      .get(`${this.BASE_API_URL}sow/${id}/export/sow-departments`)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  private closeExportDialog() {
    const data: ModalConfig = {
      title: `Export ${this.lang.get('sow')}`,
      body: 'Your download will begin shortly.',
      confirmText: 'Close',
      confirmCallback: () => dialog.close(),
      cancelCallback: undefined,
      inputs: [],
      isMemberModal: undefined,
      limitBodyWidth: true,
      customContainerClass: 'w-370',
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  private getFilenameFromUrl(url: string, name: string): string {
    if (url.endsWith('/export/docx')) return `${name}.docx`
    if (url.endsWith('/export/xlsx') || url.endsWith('/export/sow-departments')) return `${name}.xlsx`
    return name
  }
}
